import { Link } from "react-router-dom";
import { ArrowRight, Layers, Magnet, SquareRoundCorner } from "lucide-react";
import { LaminaIllustration } from "@/components/site/Lamina";
import { SwipeRow } from "@/components/site/SwipeRow";
import { FLOAT_BLOCK, ROUNDED_TEXT } from "@/content/texts";
import { dims, formatCOP, useCatalog, useSiteMedia } from "@/lib/site";

export const ProductCard = ({ p, orientation }) => {
  const d = dims(p, orientation);
  const sample = useSiteMedia()[`card-${orientation}`];
  return (
    <article data-testid={`product-card-${p.slug}`} className="group flex flex-col bg-white p-6 sm:p-8 transition-shadow duration-300 hover:shadow-[0_24px_50px_-30px_rgba(8,8,8,0.35)]">
      <LaminaIllustration w={d.w} h={d.h} maxH={210} pxPerCm={3.4}>
        {sample && <img data-testid={`product-card-${p.slug}-photo`} src={sample.url} alt={sample.alt}
          loading="lazy" className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: sample.pos || "50% 50%" }} />}
      </LaminaIllustration>
      <p className="mt-6 text-[0.7rem] uppercase tracking-[0.12em] text-[#080808]/45">Ejemplo a escala relativa</p>
      <div className="mt-3 flex items-baseline justify-between gap-3">
        <h3 className="font-serif-display text-4xl font-medium">{p.size}</h3>
        <p data-testid={`product-card-${p.slug}-dims`} className="text-sm text-[#080808]/75">{d.w} × {d.h} cm</p>
      </div>
      <p data-testid={`product-card-${p.slug}-price`} className="mt-2 text-lg font-medium">{formatCOP(p.price_cop)}</p>
      <Link to={`/productos/${p.slug}?orientacion=${orientation}`} data-testid={`product-card-${p.slug}-customize`}
        className="mt-6 inline-flex justify-center rounded-full bg-[#080808] px-6 py-3.5 text-[0.75rem] font-semibold uppercase tracking-[0.16em] text-[#FAF8F4] transition-colors hover:bg-[#B18A4B]">
        Personalizar
      </Link>
    </article>
  );
};

const SizeCard = ({ p, media }) => {
  const d = dims(p, "vertical");
  return (
    <Link to={`/personalizar?talla=${p.slug}&orientacion=vertical`} data-testid={`choose-card-${p.slug}`}
      aria-label={`Personalizar lámina ${p.size} de ${d.w} × ${d.h} cm, ${formatCOP(p.price_cop)}`}
      className="group flex h-full cursor-pointer flex-col items-center rounded-xl bg-white p-3 pb-7 text-center shadow-[0_18px_40px_-32px_rgba(8,8,8,0.35)] outline-none transition-shadow duration-300 hover:shadow-[0_28px_50px_-30px_rgba(8,8,8,0.45)] focus-visible:ring-2 focus-visible:ring-[#B18A4B] focus-visible:ring-offset-2">
      <div className="w-full overflow-hidden rounded-lg bg-[#EAE7E2]">
        {media?.url && <img data-testid={`choose-card-${p.slug}-plate`} src={media.url} alt={media.alt} loading="lazy"
          className="aspect-[4/5] w-full object-cover transition-transform [transition-duration:250ms] ease-out group-hover:scale-105 group-focus-visible:scale-105" />}
        {!media?.url && <div className="aspect-[4/5] w-full" />}
      </div>
      <h3 className="mt-4 font-serif-display text-4xl font-medium">{p.size}</h3>
      <p data-testid={`choose-card-${p.slug}-dims`} className="mt-1 text-sm text-[#080808]/70">{d.w} × {d.h} cm</p>
      <p data-testid={`choose-card-${p.slug}-price`} className="mt-2 text-lg font-medium">{formatCOP(p.price_cop)}</p>
    </Link>
  );
};

const FEATURES = [[Layers, "Efecto flotante"], [SquareRoundCorner, "Bordes redondeados"], [Magnet, "Sistema magnético"]];

export const ChooseBlock = () => {
  const { data } = useCatalog();
  const products = (data?.products || []).filter((p) => p.available);
  const media = useSiteMedia();
  return (
    <section data-testid="choose-section" aria-labelledby="choose-title" className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 py-20 sm:py-28">
      <h2 id="choose-title" className="font-serif-display text-[28px] sm:text-[40px] font-medium leading-tight">Elige tu LUMINA</h2>
      <div data-testid="choose-title-accent" className="mt-4 h-[2px] w-16 bg-[#B18A4B]" />
      <div className="mt-12">
        <SwipeRow testId="choose-row" label="Tallas LUMINA" items={products} getKey={(p) => p.slug} render={(p) => <SizeCard p={p} media={media[`size-${p.slug}`]} />} />
      </div>
      <div className="mt-10 flex justify-center">
        <Link to="/personalizar" data-testid="home-customize-button"
          className="group inline-flex items-center gap-3 rounded-full bg-[#080808] px-9 py-4 text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-[#FAF8F4] transition-colors hover:bg-[#B18A4B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B18A4B] focus-visible:ring-offset-2">
          Personaliza tu LUMINA <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </div>
      <ul data-testid="advantages-line" className="mt-14 grid grid-cols-3 divide-x divide-[#080808]/15 border-y border-[#EAE7E2] py-6">
        {FEATURES.map(([Icon, label]) => (
          <li key={label} className="flex flex-col items-center justify-center gap-2 px-2 text-center sm:flex-row sm:gap-3">
            <Icon className="h-5 w-5 text-[#B18A4B]" strokeWidth={1.5} />
            <span className="text-[0.68rem] font-medium uppercase tracking-[0.14em] sm:text-[0.78rem]">{label}</span>
          </li>
        ))}
      </ul>
      <div className="mx-auto mt-10 grid max-w-4xl gap-6 text-base leading-relaxed text-[#080808]/75 md:grid-cols-2">
        <p>{FLOAT_BLOCK}</p>
        <p>{ROUNDED_TEXT}</p>
      </div>
    </section>
  );
};
